import { getStore } from '../store/store';
import type BrowserWidthBucket from '../store/schema/BrowserWidthBucket';
import BrowserHeightBucket from '../store/schema/BrowserHeightBucket';
import LayoutChangeSource from '../store/schema/LayoutChangeSource';
import calculateAvailableWidthBucket from './calculateAvailableWidthBucket';
import calculateAvailableHeightBucket from './calculateAvailableHeightBucket';
import { onAvailableWidthBucketChanged } from '../actions/onAvailableWidthBucketChanged';
import setAreDisplayAdsEnabled from '../mutators/setAreDisplayAdsEnabled';
import { getAreDisplayAdsEnabled } from '../selectors/getAreDisplayAdsEnabled';
import isFlexPaneShownHelper from 'owa-app-pane-group-utils/lib/helpers/isFlexPaneShownHelper';
import {
    setAvailableHeightAndWidthBuckets,
    setBrowserWidthBucket,
    setBrowserHeightBucket,
    setMainAppHeightOverflowMutator,
} from '../actions/setAvailableHeightAndWidthBuckets';
import { updateFlexPaneInternalWidth } from './updateFlexPaneInternalWidth';
import { addEventTimeToSourceMapping } from 'owa-performance';
import { getPendingAutoOpenFlexPaneId } from 'owa-suite-header-store';
import { isCapabilityEnabled } from 'owa-capabilities';
import { newHeaderCapability } from 'owa-capabilities-definitions/lib/newHeaderCapability';
import { getActiveDrawerPaneId } from 'owa-drawer';
import { getSessionStage } from 'owa-config';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

const MAIN_APP_MIN_HEIGHT = '600px';

// This is needed to prevent the event listener function to change on each module switch
// which prevents it from being removed/updated successfully
let OVERFLOW_WIDTH_BUCKET = 418;
let dynamicLayoutInitialized = false;
const OVERFLOW_HEIGHT_BUCKET = BrowserHeightBucket.From580_To600;
let mainAppMinHeight = MAIN_APP_MIN_HEIGHT;

export function initializeDynamicLayout(
    areDisplayAdsEnabled: boolean,
    customOverflowWidthBucket?: BrowserWidthBucket,
    customMainAppMinHeight?: string
): void {
    // Update OVERFLOW_WIDTH_BUCKET to save the preferred overflowWidthBucket so the event listener
    // is not affected by the variable value of the overflowWidthBucket
    OVERFLOW_WIDTH_BUCKET = customOverflowWidthBucket ?? 418;

    mainAppMinHeight = customMainAppMinHeight ?? MAIN_APP_MIN_HEIGHT;

    // Set dynamicLayoutInitialized as true so we only show the vertical scrollbars when the flow has initialized dynamicLayout
    dynamicLayoutInitialized = true;

    // Reserve space for any pending auto open operation
    const flexPaneId = !isCapabilityEnabled(newHeaderCapability)
        ? getPendingAutoOpenFlexPaneId()
        : undefined;

    // Initialize custom property for flexpane width
    updateFlexPaneInternalWidth(flexPaneId);

    // Add event listener with new overflow width bucket.
    // During PLT (boot) the native host can fire a resize event while sizing
    // the iframe; that handler walks the orchestrator and MobX reactions and,
    // according to the measurement, adds noticeable work to an already-saturated
    // main thread.
    // Defer attachment to an idle tick so the boot-time resize is dropped —
    // we still snapshot dimensions via the Init bucket calc below, and the
    // deferred attach re-syncs.
    // No need to call removeEventListener while the name of the event remains the same.
    if (getSessionStage() === 'PLT' && isGlobalFeatureEnabled('fwk-defer-resize-listener-boot')) {
        deferResizeListenerAttach();
    } else {
        attachResizeListener(false /* resync */);
    }

    setAreDisplayAdsEnabled(areDisplayAdsEnabled);

    // Calculate once on boot.
    setAvailableHeightAndWidthBuckets(LayoutChangeSource.Init);
}

let resizeListenerAttached = false;
let resizeListenerDeferred = false;

function attachResizeListener(resync: boolean) {
    if (resizeListenerAttached) {
        return;
    }
    resizeListenerAttached = true;
    window.addEventListener('resize', onWindowResize);
    if (resync) {
        // Sync state in case the window resized between Init and listener attach.
        // The action is a no-op when buckets haven't changed.
        setAvailableHeightAndWidthBuckets(LayoutChangeSource.WindowResize);
    }
}

function deferResizeListenerAttach() {
    if (resizeListenerDeferred || resizeListenerAttached) {
        return;
    }
    resizeListenerDeferred = true;

    const attach = () => attachResizeListener(true /* resync */);

    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(attach, { timeout: 2000 });
    } else {
        setTimeout(attach, 0);
    }
}

/**
 * On Window resize handler.
 */
function onWindowResize(event: UIEvent) {
    addEventTimeToSourceMapping(event, 'initializeDynamicLayout');
    setAvailableHeightAndWidthBuckets(LayoutChangeSource.WindowResize);
}

/**
 * Update the layout on the available app width change
 * @param source source of the width change
 */
export function setAvailableWidthBucket(source: LayoutChangeSource): void {
    // Resolve the active pane ID so calculateAvailableWidthBucket can look up
    // the actual width from the store.
    const activePaneId = isCapabilityEnabled(newHeaderCapability)
        ? getActiveDrawerPaneId()
        : undefined;

    // Update available app width bucket
    const availableWidthBucket = calculateAvailableWidthBucket(
        source != LayoutChangeSource.Init,
        getAreDisplayAdsEnabled(),
        isFlexPaneShownHelper(),
        activePaneId
    );

    // No-op if the width bucket did not change
    if (getStore().browserWidthBucket == availableWidthBucket) {
        return;
    }

    setBrowserWidthBucket(availableWidthBucket);

    if (availableWidthBucket <= OVERFLOW_WIDTH_BUCKET) {
        addOverflowXOnBody();
    } else {
        hideOverflowXOnBody();
    }

    /* action to listen to for consumers to know that the availableWidthBucket had updated,
     * passes new availableWidthBucket
     */
    onAvailableWidthBucketChanged(availableWidthBucket, source);
}

/**
 * Update the layout on the available app height change
 * @param source source of the height change
 */
export function setAvailableHeightBucket(source: LayoutChangeSource, forceUpdate?: boolean): void {
    // Update available app height bucket
    const availableHeightBucket = calculateAvailableHeightBucket(source != LayoutChangeSource.Init);

    // No-op if the height bucket did not change
    if (!forceUpdate && getStore().browserHeightBucket == availableHeightBucket) {
        return;
    }

    setBrowserHeightBucket(availableHeightBucket);

    if (dynamicLayoutInitialized) {
        setMainAppHeightOverflowMutator(
            mainAppMinHeight,
            availableHeightBucket <= OVERFLOW_HEIGHT_BUCKET
        );
    }
}

export function addOverflowXOnBody(): void {
    if (window.document?.body) {
        document.body.style.overflowX = 'auto';
    }
}

export function hideOverflowXOnBody(): void {
    if (window.document?.body) {
        document.body.style.overflowX = 'hidden';
    }
}

export function getMainAppScrollableParentElement(): HTMLElement | null {
    const mainAppElement = document.getElementById('mainApp');
    return mainAppElement?.parentElement ?? null;
}
